/**
 * DashboardView — o Início com o MARKUP EXATO do mock aprovado
 * (design-proposals/mock-app.html · #scr-inicio), alimentado por dados reais.
 * Desktop: dash-hello + ringmini → dash-grid (Continuar · Ritual · Constelação)
 * → dbooks. Mobile: f-head → lvl-row → wkrow → msave → missões.
 * As classes vêm de src/mock-skin.css (CSS verbatim do mock).
 */
import { useTranslation } from 'react-i18next';
import { motion, useReducedMotion } from 'motion/react';
import { useNavigate } from 'react-router-dom';
import { useAppStore } from '../stores/app-store';
import { useAuthStore } from '../stores/auth-store';
import { BookCover } from './book-cover';
import { cn } from '../lib/utils';
import { LEVEL_XP, totalWordsOf, writerLevelOf, writerTitleOf } from '../lib/writer-level';

// ── Constants ────────────────────────────────────────────────────────────────
const NOVEL_TARGET = 80_000;
const DAILY_GOAL = 500;

// ── Helpers ──────────────────────────────────────────────────────────────────
function getGreeting(firstName: string, t: (key: string, fallback: string) => string): string {
  const hour = new Date().getHours();
  if (hour < 12) return t('dashboard.greetingMorning', `Bom dia, ${firstName}!`).replace('{name}', firstName);
  if (hour < 18) return t('dashboard.greetingAfternoon', `Boa tarde, ${firstName}!`).replace('{name}', firstName);
  return t('dashboard.greetingEvening', `Boa noite, ${firstName}!`).replace('{name}', firstName);
}

function deriveProgress(wordCount: number): number {
  return Math.min(100, Math.round((wordCount / NOVEL_TARGET) * 100));
}

/** Constelação da semana — métrica idêntica ao mock (x=16+i·208/6, y=70−v·52).
    `width` alarga o viewBox pra versões de tela cheia (Estatísticas). */
export function WeekConstellation({
  days,
  width = 240,
  height = 96,
  fill = false,
}: {
  days: { date: string; dayName: string; wordsWritten: number; isToday: boolean }[];
  width?: number;
  height?: number;
  /** Preenche o contêiner via estilo inline (vence qualquer CSS da pele). */
  fill?: boolean;
}) {
  const max = Math.max(...days.map((d) => d.wordsWritten), 1);
  // frações do mock (y=70/96, amplitude 52/96) + fator de escala: com o
  // viewBox 1:1 nos pixels, estrelas/linha/rótulos crescem junto da altura
  const kRaw = Math.max(1, height / 96);
  // amortecida: metade do crescimento, teto em 1.6x — mantém a delicadeza do mock
  const k = Math.min(1 + (kRaw - 1) * 0.55, 1.6);
  const baseY = height * 0.729;
  const amp = height * 0.542;
  const pad = 16 * k;
  const pts = days.map((d, i) => {
    const v = d.wordsWritten / max;
    return { ...d, v, x: pad + i * ((width - pad * 2) / 6), y: baseY - v * amp };
  });
  const rOf = (p: { wordsWritten: number; v: number }) =>
    (p.wordsWritten > 0 ? 2.4 + p.v * 3.4 : 1.8) * k;
  const reduce = useReducedMotion();
  const star = { transformBox: 'fill-box' as const, transformOrigin: 'center' };
  return (
    <svg
      className="const2"
      viewBox={`0 0 ${width} ${height}`}
      preserveAspectRatio="xMidYMid meet"
      style={fill ? { width: '100%', height: '100%', display: 'block' } : undefined}
      aria-hidden
    >
      {/* a linha se DESENHA entre as estrelas */}
      <motion.path
        fill="none"
        stroke="rgba(223,201,147,0.35)"
        strokeWidth={k}
        d={pts.map((p, i) => `${i ? 'L' : 'M'}${p.x} ${p.y}`).join(' ')}
        initial={reduce ? false : { pathLength: 0 }}
        animate={{ pathLength: 1 }}
        transition={{ delay: 0.3, duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
      />
      {pts.map((p, i) => (
        <g key={p.date}>
          {/* cada estrela ACENDE em sequência, com mola */}
          <motion.circle
            cx={p.x}
            cy={p.y}
            r={rOf(p)}
            fill={p.wordsWritten > 0 ? '#F6E9C8' : '#6B7BA8'}
            onClick={() => {
              if (p.wordsWritten <= 0) return;
              const key = `moment-wish-${new Date().toISOString().slice(0, 10)}`;
              if (localStorage.getItem(key)) return;
              localStorage.setItem(key, '1');
              window.dispatchEvent(new CustomEvent('reformu:moment', { detail: { type: 'wish' } }));
            }}
            cursor={p.wordsWritten > 0 ? 'pointer' : undefined}
            pointerEvents="auto"
            initial={reduce ? false : { scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.25 + i * 0.11, type: 'spring', stiffness: 280, damping: 13 }}
            style={{
              ...star,
              ...(p.wordsWritten > 0 ? { filter: `drop-shadow(0 0 ${5 * k}px rgba(223,201,147,0.85))` } : {}),
            }}
          />
          {p.isToday && (
            <motion.circle
              cx={p.x} cy={p.y} r={rOf(p) + 3.5 * k}
              fill="none" stroke="var(--ember)" strokeWidth={0.8 * k}
              initial={reduce ? false : { scale: 0.4, opacity: 0 }}
              animate={{ scale: 1, opacity: 0.7 }}
              transition={{ delay: 0.3 + i * 0.11 + 0.25, type: 'spring', stiffness: 200, damping: 14 }}
              style={star}
            />
          )}
          <motion.text
            x={p.x}
            y={height - 6 * k}
            textAnchor="middle"
            fontSize={7 * k}
            fill="#6B7BA8"
            fontFamily="'JetBrains Mono',monospace"
            initial={reduce ? false : { opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.9 + i * 0.05, duration: 0.4 }}
          >
            {p.dayName.replace('.', '').charAt(0).toUpperCase()}
          </motion.text>
        </g>
      ))}
    </svg>
  );
}

function Candle({ lit }: { lit: boolean }) {
  // acesa apenas quando há palavras escritas hoje; apagada = opacidade baixa
  return (
    <span className={cn('candle-mini', !lit && 'out')} aria-hidden>
      <span className="fl" />
      <span className="wk" />
      <span className="wx" />
    </span>
  );
}

// ── Component ────────────────────────────────────────────────────────────────
export function DashboardView() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const { projects, getWeekStats, getTodayStats } = useAppStore();

  // ── Data (preserve all real logic) ──────────────────────────────────────
  const weekStats = getWeekStats();
  const todayStats = getTodayStats();

  const totalWords = totalWordsOf(projects);
  const { level, xpIn, pct: levelPct } = writerLevelOf(totalWords);
  const levelTitle = writerTitleOf(level, t);

  // Streak — consecutive days with wordsWritten > 0
  const calculateStreak = (): number => {
    if (weekStats.length === 0) return 0;
    const sortedStats = [...weekStats].sort(
      (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
    );
    let streak = 0;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    for (let i = 0; i < sortedStats.length; i++) {
      const statDate = new Date(sortedStats[i].date);
      statDate.setHours(0, 0, 0, 0);
      const expectedDate = new Date(today);
      expectedDate.setDate(today.getDate() - i);
      if (statDate.getTime() === expectedDate.getTime() && sortedStats[i].wordsWritten > 0) {
        streak++;
      } else if (i === 0 && statDate.getTime() !== expectedDate.getTime()) {
        expectedDate.setDate(today.getDate() - 1);
        if (statDate.getTime() === expectedDate.getTime() && sortedStats[i].wordsWritten > 0) {
          streak++;
        } else {
          break;
        }
      } else {
        break;
      }
    }
    return streak;
  };
  const streak = calculateStreak();

  const recentProjects = [...projects]
    .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())
    .slice(0, 3);
  const heroProject = recentProjects[0] ?? null;
  const heroWordCount = heroProject
    ? heroProject.chapters.reduce((sum, c) => sum + c.wordCount, 0)
    : 0;
  const heroProgress = deriveProgress(heroWordCount);
  const otherProjects = recentProjects.slice(1);

  // 7-day calendar
  const getWeekCalendar = () => {
    const days = [];
    const today = new Date();
    for (let i = 6; i >= 0; i--) {
      const date = new Date(today);
      date.setDate(today.getDate() - i);
      const dateStr = date.toISOString().split('T')[0];
      const stat = weekStats.find((s) => s.date === dateStr);
      days.push({
        date: dateStr,
        dayName: new Date(dateStr).toLocaleDateString('pt-BR', { weekday: 'short' }),
        wordsWritten: stat?.wordsWritten ?? 0,
        isToday: i === 0,
      });
    }
    return days;
  };
  const weekCalendar = getWeekCalendar();
  const bestDay = weekCalendar.reduce(
    (best, d) => (d.wordsWritten > best.wordsWritten ? d : best),
    weekCalendar[0] ?? { date: '', dayName: '—', wordsWritten: 0, isToday: false }
  );

  const todayWords = todayStats?.wordsWritten ?? 0;
  const todayProgress = Math.min(100, Math.round((todayWords / DAILY_GOAL) * 100));
  const remaining = Math.max(0, DAILY_GOAL - todayWords);
  const candleLit = todayWords > 0;

  // Missões de hoje — dados reais
  const bestOtherNight = Math.max(
    ...weekCalendar.filter((d) => !d.isToday).map((d) => d.wordsWritten),
    0
  );
  const quests = [
    { label: t('hud.missionWrite', 'Escrever {{goal}} palavras', { goal: DAILY_GOAL }), done: todayWords >= DAILY_GOAL, xp: 120 },
    { label: t('hud.missionCandle', 'Manter a vela acesa'), done: candleLit, xp: 80 },
    { label: t('hud.missionBest', 'Superar sua melhor noite'), done: todayWords > bestOtherNight && todayWords > 0, xp: 150 },
  ];
  const questsDone = quests.filter((q) => q.done).length;

  const handleOpenProject = (projectId: string) => {
    navigate(`/app/editor/${projectId}`);
  };

  const firstName = user?.name?.split(' ')[0] || t('dashboardView.defaultWriterName', 'Escritor');
  const dateLabel = new Date().toLocaleDateString('pt-BR', { weekday: 'long', day: 'numeric', month: 'long' });
  const greeting = getGreeting(firstName, t);

  const streakSentence = !candleLit
    ? t('hud.streakOut', 'A vela está apagada — escreva {{remaining}} palavras para acendê-la.', { remaining: remaining.toLocaleString('pt-BR') })
    : todayWords >= DAILY_GOAL
      ? t('hud.streakLitDone', '{{days}} dias de ofensiva — a vela de hoje já está acesa.', { days: Math.max(streak, 1) })
      : t('hud.streakLit', '{{days}} dias de ofensiva — faltam {{remaining}} palavras para a vela seguir acesa.', { days: Math.max(streak, 1), remaining: remaining.toLocaleString('pt-BR') });

  const RING_C = 207.3;

  const heroCover = heroProject && (
    <BookCover
      title={heroProject.metadata.title || t('projects.untitled', 'Sem título')}
      author={heroProject.metadata.author || ''}
      genre={heroProject.metadata.genre || 'Fiction'}
      coverImage={heroProject.metadata.coverImage}
      variant="cover"
      progress={heroProgress}
      seed={heroProject.id}
      year={new Date(heroProject.createdAt).getFullYear()}
      disableTilt
      className="w-full"
    />
  );

  // ── Render — markup do mock, dados reais ─────────────────────────────────
  return (
    <div className="flex-1 min-h-0 overflow-y-auto md:overflow-hidden overflow-x-hidden [-webkit-overflow-scrolling:touch] animate-fade-in">
      <div className="px-4 pt-6 pb-8 md:px-[30px] md:py-[26px] max-w-2xl md:max-w-none mx-auto md:mx-0 w-full md:h-full md:flex md:flex-col">

        {/* ═══ DESKTOP · dash do mock ═══ */}
        <div className="hidden md:flex md:flex-col md:flex-1 md:min-h-0">
          <div className="dash-hello">
            <div>
              <p>{dateLabel}</p>
              <h3>{greeting}</h3>
            </div>
            <div className="ringmini" title={`${t('hud.level', 'Nível')} ${level} · ${levelTitle}`}>
              <svg viewBox="0 0 74 74">
                <defs>
                  <linearGradient id="gradEmber" x1="0" y1="0" x2="1" y2="1">
                    <stop offset="0" stopColor="#DFC993" />
                    <stop offset="1" stopColor="#F6E9C8" />
                  </linearGradient>
                </defs>
                <circle className="rbg" cx="37" cy="37" r="33" />
                <circle className="rfg" cx="37" cy="37" r="33" style={{ strokeDashoffset: RING_C * (1 - levelPct / 100) }} />
              </svg>
              <div>{level}</div>
            </div>
          </div>

          <div className="dash-grid">
            {/* Continue de onde parou */}
            <div className="dcard">
              <p className="t">{t('dashboardView.continueWhereLeftOff', 'Continue de onde parou')}</p>
              {heroProject ? (
                <div className="dcont">
                  <div className="w-16 shrink-0">{heroCover}</div>
                  <div className="min-w-0">
                    <b className="truncate">{heroProject.metadata.title || t('projects.untitled', 'Sem título')}</b>
                    <small>{heroProject.chapters.length} {t('projects.chapters', 'capítulos')}</small>
                    <div className="xp-track" style={{ marginTop: 10, width: 150 }}>
                      <div className="xp-fill" style={{ width: `${heroProgress}%` }} />
                    </div>
                    <small style={{ marginTop: 5, display: 'block' }}>
                      {heroProgress}% · {heroWordCount.toLocaleString('pt-BR')} {t('projects.words', 'palavras')}
                    </small>
                    <button className="go" onClick={() => handleOpenProject(heroProject.id)}>
                      ▶ {t('hud.continue', 'Continuar')}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="py-4">
                  <b className="m-title block text-[18px] mb-3">
                    {t('dashboardView.blankPageLine1', 'Uma página em branco')} {t('dashboardView.blankPageLine2', 'é um convite.')}
                  </b>
                  <button className="go" onClick={() => navigate('/app/create')}>
                    + {t('dashboardView.createFirstBook', 'Criar meu primeiro livro')}
                  </button>
                </div>
              )}
            </div>

            {/* O ritual de hoje */}
            <div className="dcard">
              <p className="t">{t('dashboardView.todaysRitual', 'O ritual de hoje')}</p>
              <p className="ritual-num">
                {todayWords.toLocaleString('pt-BR')}
                <small> / {DAILY_GOAL} {t('projects.words', 'palavras')}</small>
              </p>
              <div className="xp-track" style={{ margin: '12px 0 10px' }}>
                <div className="xp-fill" style={{ width: `${todayProgress}%` }} />
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 11 }}>
                <Candle lit={candleLit} />
                <p style={{ fontSize: '11.5px', lineHeight: 1.55 }} className="text-muted-foreground">
                  {streakSentence}
                </p>
              </div>
            </div>

            {/* Constelação da semana */}
            <div className="dcard">
              <p className="t">{t('hud.constellation', 'Constelação da semana')}</p>
              <WeekConstellation days={weekCalendar} />
              <p className="font-mono text-[10px] uppercase tracking-[0.14em] text-muted-foreground text-center mt-1">
                {t('hud.bestNight', 'melhor noite')}: {bestDay.wordsWritten > 0
                  ? `${bestDay.dayName.replace('.', '')} · ${bestDay.wordsWritten.toLocaleString('pt-BR')}`
                  : '—'}
              </p>
            </div>

            {/* Outros manuscritos */}
            {otherProjects.length > 0 && (
              <div className="dbooks">
                {otherProjects.map((project) => {
                  const wordCount = project.chapters.reduce((sum, c) => sum + c.wordCount, 0);
                  const pProgress = deriveProgress(wordCount);
                  return (
                    <button key={project.id} className="dbook" onClick={() => handleOpenProject(project.id)}>
                      <div className="shrink-0">
                        <BookCover
                          title={project.metadata.title || t('projects.untitled', 'Sem título')}
                          author={project.metadata.author || ''}
                          genre={project.metadata.genre || 'Fiction'}
                          coverImage={project.metadata.coverImage}
                          variant="spine"
                          progress={pProgress}
                          seed={project.id}
                          thickness={26}
                          height={44}
                          year={new Date(project.createdAt).getFullYear()}
                        />
                      </div>
                      <span className="min-w-0">
                        <b className="truncate">{project.metadata.title || t('projects.untitled', 'Sem título')}</b>
                        <small>{wordCount.toLocaleString('pt-BR')} {t('projects.words', 'palavras')}</small>
                      </span>
                      <span className="pct" style={pProgress >= 100 ? { color: 'var(--aurora)' } : undefined}>
                        {pProgress}%
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* ═══ MOBILE · fone 01 do mock ═══ */}
        <div className="md:hidden">
          <div className="f-head">
            <p className="m-eyebrow">{dateLabel}</p>
            <h5 className="m-title">{greeting}</h5>
          </div>

          <div className="lvl-row">
            <span className="lvl-badge">{level}</span>
            <div className="lvl-info">
              <small>
                <span>{t('hud.level', 'Nível')} {level} · {levelTitle}</span>
                <span>{xpIn.toLocaleString('pt-BR')}/{LEVEL_XP.toLocaleString('pt-BR')}</span>
              </small>
              <div className="xp-track"><div className="xp-fill" style={{ width: `${levelPct}%` }} /></div>
            </div>
            <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
              <Candle lit={candleLit} />
              <b className="font-mono text-[9px]" style={{ color: 'var(--ember-hot)' }}>{streak}</b>
            </span>
          </div>

          <div className="wkrow">
            <small>{t('hud.week', 'Semana')}</small>
            {weekCalendar.map((d) => {
              const max = Math.max(...weekCalendar.map((x) => x.wordsWritten), 1);
              const size = d.wordsWritten > 0 ? 6 + (d.wordsWritten / max) * 4 : 4.5;
              return (
                <i
                  key={d.date}
                  className={cn('wst', d.wordsWritten > 0 && 'on')}
                  style={{ width: size, height: size }}
                  title={`${d.dayName} · ${d.wordsWritten.toLocaleString('pt-BR')}`}
                />
              );
            })}
          </div>

          {heroProject ? (
            <div className="msave">
              <div className="w-11 shrink-0">{heroCover}</div>
              <div className="min-w-0">
                <p className="m-eyebrow" style={{ marginBottom: 3 }}>
                  {t('dashboardView.continueWhereLeftOff', 'Continue de onde parou')}
                </p>
                <b className="truncate block">{heroProject.metadata.title || t('projects.untitled', 'Sem título')}</b>
                <small>{heroProgress}% · {heroWordCount.toLocaleString('pt-BR')} {t('projects.words', 'palavras')}</small>
                <div>
                  <button className="go" onClick={() => handleOpenProject(heroProject.id)}>
                    ▶ {t('hud.continue', 'Continuar')}
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="msave">
              <div className="min-w-0 py-1">
                <b>{t('dashboardView.blankPageLine1', 'Uma página em branco')} {t('dashboardView.blankPageLine2', 'é um convite.')}</b>
                <div>
                  <button className="go" onClick={() => navigate('/app/create')}>
                    + {t('dashboardView.createFirstBook', 'Criar meu primeiro livro')}
                  </button>
                </div>
              </div>
            </div>
          )}

          <p className="mq-title">
            {t('hud.missionsToday', 'Missões de hoje')} <span>{questsDone}/{quests.length}</span>
          </p>
          {quests.map((q) => (
            <div key={q.label} className={cn('mquest', q.done && 'done')}>
              <span className="c">{q.done ? '✓' : ''}</span>
              <span>{q.label}</span>
              <span className="x">+{q.xp}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
